import React, { useId } from 'react';
import { Box, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { ANNOTATION_CATEGORIES, getAnnotationCategory } from '../../utils/annotationCategories';

interface CategorySelectProps {
  value: string;
  onChange: (categoryId: string) => void;
  disabled?: boolean;
  minWidth?: number;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
}

/** The category's colour dot and its name in ink (the colour stays on the dot). */
const CategoryOption: React.FC<{ id: string }> = ({ id }) => {
  const category = getAnnotationCategory(id);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
      <Box aria-hidden="true" sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: category.color, flexShrink: 0 }} />
      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {category.label}
      </Box>
    </Box>
  );
};

/** Category picker with the shared colours. */
const CategorySelect: React.FC<CategorySelectProps> = ({
  value,
  onChange,
  disabled = false,
  minWidth = 168,
  size = 'small',
  fullWidth = false,
}) => {
  const labelId = useId();
  const known = ANNOTATION_CATEGORIES.some((category) => category.id === value);
  return (
    <FormControl size={size} sx={{ minWidth }} fullWidth={fullWidth} disabled={disabled}>
      <InputLabel id={labelId}>Category</InputLabel>
      <Select
        labelId={labelId}
        label="Category"
        value={value}
        onChange={(event) => onChange(String(event.target.value))}
        renderValue={(selected) => <CategoryOption id={String(selected)} />}
        MenuProps={{ PaperProps: { sx: { mt: 0.5 } } }}
      >
        {ANNOTATION_CATEGORIES.map((category) => (
          <MenuItem key={category.id} value={category.id}>
            <CategoryOption id={category.id} />
          </MenuItem>
        ))}
        {!known && value && (
          <MenuItem value={value}>
            <CategoryOption id={value} />
          </MenuItem>
        )}
      </Select>
    </FormControl>
  );
};

export default CategorySelect;
